import { useState } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { ERequestType } from "../../Enum/ERequestType"
import type {
  CreateRestaurantReservation,
  PublicRestaurantReservationConfiguration,
  RestaurantReservation,
  RestaurantReservationArea,
  RestaurantReservationManagement,
  RestaurantReservationSettings,
  RestaurantReservationSlot,
  SaveManagementRestaurantReservation,
} from "../../Objects/RestaurantReservation/RestaurantReservationTypes"
import { normalizeRestaurantReservation } from "../../Objects/RestaurantReservation/RestaurantReservationTypes"
import { DeleteRequest, GetRequest, PostRequest, PutFormRequest, PutRequest } from "../../services/ApiClientService"

export const useRestaurantReservations = () => {
  const { t } = useTranslation("general")

  const { setError } = useError()

  const [isLoading, setIsLoading] = useState(false)

  const run = async <T>(operation: () => Promise<T>) => {
    setIsLoading(true)

    try {
      return await operation()
    } catch (requestError) {
      const message =
        requestError instanceof Error && requestError.message.trim()
          ? requestError.message
          : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  const getPublicConfiguration = (slug: string) =>
    run(async () => {
      const response = await GetRequest<PublicRestaurantReservationConfiguration>(
        API_ROUTES.RESTAURANT_RESERVATIONS.PUBLIC_CONFIGURATION(slug),
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const getAvailability = (slug: string, areaId: number, date: string, partySize: number) =>
    run(async () => {
      const response = await GetRequest<RestaurantReservationSlot[]>(
        API_ROUTES.RESTAURANT_RESERVATIONS.PUBLIC_AVAILABILITY(slug, areaId, date, partySize),
      )

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const create = (slug: string, request: CreateRestaurantReservation) =>
    run(async () => {
      const response = await PostRequest<void>(
        API_ROUTES.RESTAURANT_RESERVATIONS.PUBLIC_CREATE(slug),
        request,
        ERequestType.Customer,
      )

      if (!response.success) throw new Error(response.message)

      return true
    })

  const getMine = (slug: string) =>
    run(async () => {
      const response = await GetRequest<RestaurantReservation[]>(
        API_ROUTES.RESTAURANT_RESERVATIONS.PUBLIC_MINE(slug),
        ERequestType.Customer,
      )

      if (!response.success) throw new Error(response.message)

      return (response.data ?? []).map(normalizeRestaurantReservation)
    })

  const cancelMine = (slug: string, id: number) =>
    run(async () => {
      const response = await PostRequest<void>(
        API_ROUTES.RESTAURANT_RESERVATIONS.PUBLIC_CANCEL(slug, id),
        {},
        ERequestType.Customer,
      )

      if (!response.success) throw new Error(response.message)

      return true
    })

  const getManagement = () =>
    run(async () => {
      const response = await GetRequest<RestaurantReservationManagement>(API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT)

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const saveSettings = (request: RestaurantReservationSettings) =>
    run(async () => {
      const response = await PutRequest<RestaurantReservationSettings>(
        API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT_SETTINGS,
        request,
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const saveArea = (request: RestaurantReservationArea, icon?: File | null) =>
    run(async () => {
      const response = await PutFormRequest<RestaurantReservationArea>(
        API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT_AREAS,
        {
          id: request.id,
          name: request.name,
          capacity: request.capacity,
          isActive: request.isActive,
          displayOrder: request.displayOrder,
          icon,
        },
      )

      if (!response.success) throw new Error(response.message)

      return response.data
    })

  const deleteArea = (id: number) =>
    run(async () => {
      const response = await DeleteRequest<void>(API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT_DELETE_AREA(id))

      if (!response.success) throw new Error(response.message)

      return true
    })

  const getAll = (startDate: string, endDate: string) =>
    run(async () => {
      const response = await GetRequest<RestaurantReservation[]>(
        API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT_RESERVATIONS(startDate, endDate),
      )

      if (!response.success) throw new Error(response.message)

      return (response.data ?? []).map(normalizeRestaurantReservation)
    })

  const getManagementAvailability = (areaId: number, date: string, partySize: number, reservationId?: number) =>
    run(async () => {
      const response = await GetRequest<RestaurantReservationSlot[]>(
        API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT_AVAILABILITY(areaId, date, partySize, reservationId),
      )

      if (!response.success) throw new Error(response.message)

      return response.data ?? []
    })

  const saveManagementReservation = (request: SaveManagementRestaurantReservation) =>
    run(async () => {
      const response = await PutRequest<void>(API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT_SAVE_RESERVATION, request)

      if (!response.success) throw new Error(response.message)

      return true
    })

  const cancelManagement = (id: number) =>
    run(async () => {
      const response = await PostRequest<void>(API_ROUTES.RESTAURANT_RESERVATIONS.MANAGEMENT_CANCEL(id), {})
      if (!response.success) throw new Error(response.message)
      return true
    })

  return {
    isLoading,
    getPublicConfiguration,
    getAvailability,
    create,
    getMine,
    cancelMine,
    getManagement,
    saveSettings,
    saveArea,
    deleteArea,
    getAll,
    getManagementAvailability,
    saveManagementReservation,
    cancelManagement,
  }
}
